/**
 * Donor-cell shallow-water solver, V1. NUMERICAL OWNERSHIP ONLY.
 *
 * This module knows nothing about meshes, materials, colours, debug modes,
 * cameras or lighting. A headless numerical consumer — the physics validation
 * suite is exactly that — can instantiate it and run a full scenario without
 * ever creating a water mesh. That separation is the point: the renderer can
 * be replaced, and this solver can be replaced by a conservative two-pass or
 * dynamic-wave solver, without either touching the other.
 *
 * Fully type-checked: no `@ts-nocheck`. three's TSL typings turned out to be
 * complete enough to express the whole kernel without escape hatches, so
 * `implements FloodSolver` here is genuine compiler enforcement rather than a
 * comment (which is what it amounted to in the previous mixed module, since
 * that file carried a file-level `@ts-nocheck`).
 *
 * CANONICAL STATE OWNED HERE
 *   depthTextureA / depthTextureB   R32Float StorageTexture, GPU ping-pong
 *   seedTexture                     R32Float, one-time initial-condition upload
 *
 * REFERENCED, NOT OWNED
 *   bedTexture                      created and disposed by the caller
 *
 * WHY fp32 AND NOT fp16: measured, not assumed. At 50 mm/h rain with a 2 s
 * substep each step adds 2.78e-5 m of depth. In half-float that increment
 * falls below epsilon once depth exceeds ~0.5 m, at which point the water
 * STOPS ACCUMULATING ENTIRELY. Rendering precision is a preference; this is a
 * correctness floor.
 *
 * WHY textureLoad AND NEVER texture() IN THE KERNEL: outside the fragment
 * stage TSL lowers `texture(tex, uv)` to `textureSampleLevel` — hardware
 * bilinear (WGSLNodeBuilder.js:236-266). Interpolating the numerical stencil
 * would corrupt the solver while still looking plausible on screen. The
 * `loadCell` vs `sample*` naming split in flood-fields.ts exists to make that
 * mistake visible at the call site.
 */
import * as THREE from 'three/webgpu'
import {
  Fn, instanceIndex, uniform, ivec2, uvec2, vec4, mix, step,
  max as tslMax, textureStore,
} from 'three/tsl'

import type { FloodForcing, FloodSolver, FloodSolverOutputs } from './flood-solver'
import { cellOfIndex, clampCellComponent, loadCell, createScalarField } from './flood-fields'

/**
 * Builds a dispatchable compute kernel. Wrapping the TSL call gives the kernel
 * type a single inferred definition (`ComputeKernel` below) — three does not
 * re-export `ComputeNode` from its public entry points, so inferring it here
 * is what keeps this module free of `any`.
 */
function buildComputeKernel(body: () => void, cellCount: number) {
  return Fn(body)().compute(cellCount)
}
type ComputeKernel = ReturnType<typeof buildComputeKernel>

export interface DonorCellSolverOptions {
  gridSize: number
  cellSizeM: number
  /**
   * Static hydraulic floor (conditioned terrain + buildings), R32Float.
   * Referenced, never disposed here — the caller creates it and may share it
   * with a renderer, so a single disposal owner outside this class is what
   * keeps that unambiguous.
   */
  bedTexture: THREE.Texture
  /**
   * Spread rate (m/s-ish). Default 12 is derived, not eyeballed: linearizing
   * the donor-cell exchange around a local mean depth h̄ gives an effective
   * diffusivity D ≈ conductance·h̄, and a spread distance over a storm of
   * duration t of L ≈ √(D·t). Solving for a 50m basin to redistribute within
   * a 3600s storm at a typical shallow depth h̄=0.05m: conductance ≈
   * (50²/3600)/0.05 ≈ 13.9; 12 is the same order, rounded down for margin.
   */
  conductance?: number
  /** Uniform starting depth (m) for every cell. Ignored when `initialDepthGrid` is given. */
  initialDepthM?: number
  /** Full per-cell starting depth (m), row-major. Overrides `initialDepthM`. */
  initialDepthGrid?: Float32Array
}

export class DonorCellSolverV1 implements FloodSolver {
  readonly gridSize: number
  /**
   * Largest per-step dt this instance's conductance+cellSize can integrate
   * explicitly without oscillating, derived from the explicit-diffusion
   * stability bound dt ≲ cellSize²/(4·D), D = conductance·h̄, against a
   * conservative worst-case depth (2m) and a 0.25 safety factor.
   */
  readonly maxStableDt: number

  private readonly bedTextureRef: THREE.Texture
  private readonly depthTextureA: THREE.StorageTexture
  private readonly depthTextureB: THREE.StorageTexture
  private readonly seedTexture: THREE.DataTexture

  private readonly stepAtoB: ComputeKernel
  private readonly stepBtoA: ComputeKernel
  private readonly seedDepth: ComputeKernel

  private readonly dtUniform = uniform(1 / 30)
  private readonly rainfallUniform = uniform(0)
  private readonly infiltrationUniform = uniform(0)

  private currentIsA = true
  private depthSeeded = false

  /**
   * Published fields. V1 is honestly depth-only: no analysis textures are
   * allocated, and no capability is advertised that isn't implemented.
   *
   * `depth` is a GETTER, not a captured value. Capturing `this.depthTextureA`
   * here would freeze the output to A and render stale water on every other
   * step — the exact stale-A/B failure earlier phases eliminated, which an
   * outputs abstraction is the natural place to reintroduce. Built once in the
   * constructor so accessing `outputs` allocates nothing per frame.
   */
  private readonly outputsObject: FloodSolverOutputs

  constructor(options: DonorCellSolverOptions) {
    const { gridSize, cellSizeM, bedTexture } = options
    this.gridSize = gridSize
    this.bedTextureRef = bedTexture
    const cellCount = gridSize * gridSize
    const conductance = options.conductance ?? 12

    const MAX_EXPECTED_DEPTH_M = 2.0
    const STABILITY_SAFETY_FACTOR = 0.25
    this.maxStableDt = (cellSizeM * cellSizeM) / (4 * conductance * MAX_EXPECTED_DEPTH_M) * STABILITY_SAFETY_FACTOR

    const initialDepth = options.initialDepthGrid
      ? Float32Array.from(options.initialDepthGrid)
      : new Float32Array(cellCount)
    if (!options.initialDepthGrid && options.initialDepthM) initialDepth.fill(options.initialDepthM)

    // A StorageTexture has no CPU data channel, so the initial condition
    // arrives as an ordinary DataTexture and is copied across by a one-time
    // kernel. Retained (not disposed after seeding) so `reset()` can re-run it
    // without reallocating, and to avoid racing three's lazy upload.
    this.seedTexture = createScalarField(initialDepth, gridSize, 'DepthSeed')

    const makeDepthTexture = (name: string) => {
      const tex = new THREE.StorageTexture(gridSize, gridSize)
      tex.name = name
      // StorageTexture's constructor takes no format/type and inherits
      // RGBAFormat + UnsignedByteType from Texture (Texture.js:47) — i.e.
      // rgba8unorm, useless for physics. Both MUST be set explicitly.
      tex.format = THREE.RedFormat
      tex.type = THREE.FloatType
      tex.magFilter = THREE.LinearFilter
      tex.minFilter = THREE.LinearFilter
      // No mip chain: a single level means mip selection cannot vary with the
      // camera, so the physical field is camera-independent by construction.
      tex.generateMipmaps = false
      tex.wrapS = THREE.ClampToEdgeWrapping
      tex.wrapT = THREE.ClampToEdgeWrapping
      return tex
    }

    // Double-buffered so the update kernel reads each cell's FOUR neighbours
    // from a resource it is NOT also writing in that dispatch. WebGPU gives no
    // ordering/visibility guarantee between invocations of one dispatch
    // without an explicit barrier, so reading a neighbour another invocation
    // is concurrently writing is undefined behaviour, not a style concern.
    this.depthTextureA = makeDepthTexture('DepthA')
    this.depthTextureB = makeDepthTexture('DepthB')

    const seedTexture = this.seedTexture
    const depthTextureA = this.depthTextureA
    const depthTextureB = this.depthTextureB

    // Seeds BOTH ping-pong textures. B is fully overwritten by the first
    // dispatch and so does not strictly need it, but seeding both means no
    // code path can ever observe an uninitialized texture — a deterministic
    // initialization requirement, and cheap since it runs once.
    this.seedDepth = buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const v = loadCell(seedTexture, cell)
      const store = uvec2(cell.x, cell.y)
      textureStore(depthTextureA, store, vec4(v, 0, 0, 1)).toWriteOnly()
      textureStore(depthTextureB, store, vec4(v, 0, 0, 1)).toWriteOnly()
    }, cellCount)

    const conductanceUniform = uniform(conductance)
    const cellSizeUniform = uniform(cellSizeM)
    const dtUniform = this.dtUniform
    const rainfallUniform = this.rainfallUniform
    const infiltrationUniform = this.infiltrationUniform

    /**
     * THE DONOR-CELL EXPRESSION. Operand order, operation order, clamp
     * placement, donor weighting, the north/south/east/west summation order,
     * conductance scaling and rainfall/infiltration application are all
     * byte-for-byte what the frozen validation baseline was produced with.
     * Anything changing here is a bug, not a refactor.
     *
     * Built twice — reading A/writing B, and reading B/writing A — so every
     * dispatch reads a texture nothing in that same dispatch writes.
     */
    const buildUpdateKernel = (readDepth: THREE.Texture, writeDepth: THREE.StorageTexture) => buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const x = cell.x
      const y = cell.y

      const loadBed = (cx: typeof x, cy: typeof y) => loadCell(this.bedTextureRef, ivec2(cx, cy))
      const loadDepth = (cx: typeof x, cy: typeof y) => loadCell(readDepth, ivec2(cx, cy))

      const bed = loadBed(x, y).toVar()
      const h = loadDepth(x, y).toVar()
      const surface = bed.add(h).toVar()

      // Clamp-to-edge neighbourhood, matching ClampToEdgeWrapping on the
      // textures: north/south clamp y, east/west clamp x.
      const leftX = clampCellComponent(x.sub(1), gridSize)
      const rightX = clampCellComponent(x.add(1), gridSize)
      const bottomY = clampCellComponent(y.sub(1), gridSize)
      const topY = clampCellComponent(y.add(1), gridSize)

      // Donor-cell exchange: whichever side has the higher surface is the
      // donor, and the exchange is weighted by THAT side's own water depth —
      // a dry donor (h=0) contributes exactly zero, no matter how much higher
      // its bare terrain is (this is what keeps dry peaks dry).
      const exchangeWith = (nx: typeof x, ny: typeof y) => {
        const surfN = loadBed(nx, ny).add(loadDepth(nx, ny))
        const hN = loadDepth(nx, ny)
        const donorDepth = mix(h, hN, step(surface, surfN))
        return surfN.sub(surface).mul(donorDepth)
      }

      const netExchange = exchangeWith(x, topY)
        .add(exchangeWith(x, bottomY))
        .add(exchangeWith(rightX, y))
        .add(exchangeWith(leftX, y))

      const dh = tslMax(
        conductanceUniform.mul(dtUniform).mul(netExchange).div(cellSizeUniform.mul(cellSizeUniform)),
        h.negate(),
      )

      const newDepth = tslMax(0, h.add(dh).add(rainfallUniform).sub(infiltrationUniform))
      textureStore(writeDepth, uvec2(x, y), vec4(newDepth, 0, 0, 1)).toWriteOnly()
    }, cellCount)

    this.stepAtoB = buildUpdateKernel(depthTextureA, depthTextureB)
    this.stepBtoA = buildUpdateKernel(depthTextureB, depthTextureA)

    // Arrow closes over `this`; the literal's own getter would not.
    const currentDepth = () => this.depthTexture
    this.outputsObject = {
      get depth(): THREE.Texture { return currentDepth() },
      // flux / maxDepth / arrivalTime / duration are deliberately ABSENT.
      // Donor-cell computes none of them, and presence is the capability
      // signal — an `undefined` placeholder would still read as "offered".
    }
  }

  /** @see FloodSolver.outputs — V1 publishes depth only. */
  get outputs(): FloodSolverOutputs {
    return this.outputsObject
  }

  /** The ping-pong texture holding the latest state. No exported copy exists. */
  get depthTexture(): THREE.Texture {
    return this.currentIsA ? this.depthTextureA : this.depthTextureB
  }

  /** Static hydraulic floor. Referenced here; the caller owns and disposes it. */
  get bedTexture(): THREE.Texture {
    return this.bedTextureRef
  }

  /** The one-time IC upload, prepended to the first dispatch and never again. */
  private seedBatch(): unknown[] {
    if (this.depthSeeded) return []
    this.depthSeeded = true
    return [this.seedDepth]
  }

  advance(
    webgpuRenderer: THREE.WebGPURenderer,
    dtSeconds: number,
    forcing: FloodForcing,
    substeps = 1,
  ): void {
    this.dtUniform.value = dtSeconds
    this.rainfallUniform.value = forcing.rainfallDepthM
    this.infiltrationUniform.value = forcing.infiltrationDepthM

    // Seed must precede the first physics kernel; array order is execution order.
    const batch: unknown[] = [...this.seedBatch()]
    for (let i = 0; i < substeps; i += 1) {
      batch.push(this.currentIsA ? this.stepAtoB : this.stepBtoA)
      // Reference/state selection only — never a texture copy.
      this.currentIsA = !this.currentIsA
    }
    // Synchronous submission. Requires the caller to have awaited
    // `webgpuRenderer.init()` during setup; `compute()` otherwise warns and
    // falls back to the async path (Renderer.js:2339-2345).
    webgpuRenderer.compute(batch as Parameters<THREE.WebGPURenderer['compute']>[0])
  }

  /**
   * Returns depth to the initial condition. Re-arms the one-time seed kernel
   * (the IC still lives in `seedTexture`) and resets ping-pong parity, so the
   * next `advance()` starts from a clean, deterministic state without
   * rebuilding any GPU resource or node graph.
   */
  reset(): void {
    this.depthSeeded = false
    this.currentIsA = true
  }

  /**
   * Reads the CURRENT depth texture back to the CPU for validation/analysis.
   * NEVER called on an animation path — production GPU->CPU readback stays at
   * zero.
   *
   * Two mechanics matter and both were verified on hardware before being
   * relied on:
   *  - `copyTextureToBuffer` pads every row to a 256-byte boundary
   *    (WebGPUTextureUtils.js:539). At gridSize=32 an r32float row is 128 B
   *    and IS padded, so a naive read is silently wrong — the validation suite
   *    runs exactly that size. Rows are de-padded below.
   *  - R32Float returns a `Float32Array` directly
   *    (WebGPUTextureUtils.js:1060), so no half-float bit decoding is needed.
   */
  async readDepthBuffer(webgpuRenderer: THREE.WebGPURenderer): Promise<Float32Array> {
    if (!this.depthSeeded) {
      // Before the first dispatch the texture has no GPU resource yet.
      // Failing loudly beats returning zeros that would read as a physically
      // plausible "dry domain" and quietly corrupt a validation baseline.
      throw new Error('DonorCellSolverV1.readDepthBuffer: no compute dispatched yet — depth textures are unseeded.')
    }
    const size = this.gridSize
    // `backend` is intentionally untyped in three's public Renderer surface;
    // this is the only escape hatch in the module and is confined to one line.
    const backend = (webgpuRenderer as unknown as {
      backend: { copyTextureToBuffer(t: THREE.Texture, x: number, y: number, w: number, h: number, f: number): Promise<Float32Array> }
    }).backend
    const raw = await backend.copyTextureToBuffer(this.depthTexture, 0, 0, size, size, 0)

    const BYTES_PER_TEXEL = 4 // r32float, single channel
    const paddedBytesPerRow = Math.ceil((size * BYTES_PER_TEXEL) / 256) * 256
    const floatsPerPaddedRow = paddedBytesPerRow / 4
    if (floatsPerPaddedRow === size) return Float32Array.from(raw.subarray(0, size * size))

    const out = new Float32Array(size * size)
    for (let row = 0; row < size; row += 1) {
      const src = row * floatsPerPaddedRow
      out.set(raw.subarray(src, src + size), row * size)
    }
    return out
  }

  /** Disposes ONLY solver-owned resources. `bedTexture` belongs to the caller. */
  dispose(): void {
    this.depthTextureA.dispose()
    this.depthTextureB.dispose()
    this.seedTexture.dispose()
  }
}
